import { Grid, GridColumn, Separator, Spinner } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { Transaction } from '../../../stores/models/Transaction';
import { IPaymentDetailsStore } from '../../../stores/PaymentDetailsStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import BoxNotice from '../../common/BoxNotice';
import InfoNotice from '../notices/InfoNotice';
import WarningNotice from '../notices/WarningNotice';

import ReceiptDetails from './ReceiptDetails';
import TransactionDetailsCard from './TransactionDetailsCard';

interface IPaymentDetailsProps {
  bindingId: number;
  transactionAmount: number;
  paymentDetailsStore?: IPaymentDetailsStore;
  translateStore?: ITranslateStore;
}

export const paymentDetailsRow = (transaction: Transaction): JSX.Element => {
  if (transaction.receiptBindingId !== null) {
    return <PaymentDetails bindingId={transaction.receiptBindingId} transactionAmount={transaction.amount} />;
  } else if (transaction.receiptDescription !== null && transaction.receiptDescription !== '') {
    return <ReceiptDetails transaction={transaction} />;
  }
  return <InfoNotice translationKey="shared.transactions.notices.transaction-details-not-available" />;
};

@inject('paymentDetailsStore', 'translateStore')
@observer
class PaymentDetails extends React.Component<IPaymentDetailsProps> {
  public render(): JSX.Element {
    const paymentDetails = this.props.paymentDetailsStore.paymentDetails(this.props.bindingId);
    const translate = this.props.translateStore.translate;

    if (paymentDetails == null) {
      return null;
    }

    if (paymentDetails.isInitialLoading) {
      return <Spinner />;
    }

    if (paymentDetails.isFinishedWithError) {
      return <WarningNotice translationKey="payment-history.errors.loading-payment-details-failed" />;
    }

    if (paymentDetails.payload.departmentCode) {
      return (
        <TransactionDetailsCard title={translate('payment-history.payment-details.title')}>
          <Grid>
            <GridColumn>
              <Separator spacing="xxsmall" color="invisible" />
              <p className="text-small">{translate('payment-history.payment-details.description')}</p>
              <p>{translate('payment-history.payment-details.paid-in-store')}</p>
            </GridColumn>
          </Grid>
        </TransactionDetailsCard>
      );
    }

    return (
      <TransactionDetailsCard title={translate('payment-history.payment-details.title')}>
        {paymentDetails.payload.amount !== this.props.transactionAmount ? (
          <>
            <Separator spacing="xxsmall" color="invisible" />
            <BoxNotice type="error">
              {translate('payment-history.errors.inconsistent-transactions-amount', {
                paymentDetailsAmount: paymentDetails.payload.amount
              } as any)}
            </BoxNotice>
          </>
        ) : null}
        <Separator spacing="xxsmall" color="invisible" />
        <Grid>
          <GridColumn width={['sm-6', 'md-4']}>
            <p className="text-small">{translate('payment-history.payment-details.sender-name')}</p>
            <p>{paymentDetails.payload.senderName}</p>
          </GridColumn>
          <GridColumn width={['sm-6', 'md-4']}>
            <p className="text-small">{translate('payment-history.payment-details.sender-account')}</p>
            <p>{paymentDetails.payload.senderAccount}</p>
          </GridColumn>
          <GridColumn width={['sm-6', 'md-4']}>
            <p className="text-small">{translate('payment-history.payment-details.description')}</p>
            <p>{paymentDetails.payload.description}</p>
          </GridColumn>
          <GridColumn width={['sm-6', 'md-4']}>
            <p className="text-small">{translate('payment-history.payment-details.reference-number')}</p>
            <p>{paymentDetails.payload.referenceNumber}</p>
          </GridColumn>
          <GridColumn width={['sm-6', 'md-4']}>
            <p className="text-small">{translate('payment-history.payment-details.receiver-name')}</p>
            <p>{paymentDetails.payload.receiverName}</p>
          </GridColumn>
          <GridColumn width={['sm-6', 'md-4']}>
            <p className="text-small">{translate('payment-history.payment-details.receiver-account')}</p>
            <p>{paymentDetails.payload.receiverAccount}</p>
          </GridColumn>
        </Grid>
      </TransactionDetailsCard>
    );
  }
}

export default PaymentDetails;
